import { queryOptions, useMutation, useQueries, useQuery } from '@tanstack/react-query';
import type { AxiosError, AxiosResponse } from 'axios';
import _ from 'lodash';
import pino from 'pino';

import { teamFeaturesConstants } from '../constants/appConstants';
import type { TeamProperties } from '../repositories/requestRepository';
import { TeamRepository } from '../repositories/teamRepository';
import {
	UserSettingRepository,
	type UserSettingRequest,
	type UserSettingResponse,
} from '../repositories/userSettingRepository';
import { queryClient } from '../router/queryClient';
import { isSupportAdmin } from '../selectors/userSelectors';
import { setTime } from '../utils/setTime';
import { useZustand } from '../zustandStore';
import type { OptionalTeamProperties, PhoneDetails } from '../zustandStore/slices';

import { paymentKeys } from './paymentQuery';
import { teamKeys, userKeys } from './query-keys';
import { userQuery } from './userQuery';

const logger = pino();

export type Role = {
	roleId: string;
	securityGroupId: string;
	userId: string;
};

export type User = {
	avatarUrl?: string;
	isLastAdmin?: boolean;
	hasOpenRequests?: boolean;
	requesters: unknown[];
	companyAccountId: string;
	companyName: string;
	email: string;
	firstName: string;
	fullName: string;
	initials: string;
	language: string;
	lastName: string;
	roleId: string;
	roles: Role[];
	state: string;
	userId: string;
	teams: Record<string, unknown>[];
	phoneDetails: PhoneDetails | null;
};

export const useCreateTeamQuery = () => {
	const { setTeamId } = useZustand();
	return useMutation({
		mutationFn: TeamRepository.createTeam,
		onSuccess: data => {
			setTeamId(data?.teamId);
			return queryClient.invalidateQueries({ queryKey: [teamKeys.getTeam] });
		},
	});
};

export const useSaveTeamNameQuery = () => {
	return useMutation<unknown, unknown, { teamId: string; teamName: string }>({
		mutationFn: TeamRepository.saveTeamName,
		onSuccess: () => {
			return queryClient.invalidateQueries({ queryKey: [teamKeys.getTeam] });
		},
	});
};

export const useSetCurrentTeamByIdQuery = () => {
	return useMutation({
		mutationFn: UserSettingRepository.saveUserSetting,
		onSuccess: () => {
			return queryClient.invalidateQueries({ queryKey: [userKeys.getUserSettings, 'settingName'] });
		},
	});
};

export const useSaveTeamPropertyQuery = () => {
	return useMutation({
		mutationFn: TeamRepository.saveTeamProperty,
		onSuccess: async () => {
			void queryClient.invalidateQueries({ queryKey: teamKeys.getTeams({}) });
			void queryClient.invalidateQueries({ queryKey: [teamKeys.getTeamProperty] });
			void queryClient.invalidateQueries({ queryKey: [teamKeys.getSelectedTeamProperty] });
		},
	});
};

export const useSetUserTeamPropertyQuery = () => {
	return useMutation<void, unknown, { userId: string; teamId: string; propertyKey: string; propertyValue: string }>({
		mutationFn: ({ userId, teamId, propertyKey, propertyValue }) =>
			TeamRepository.setUserTeamProperty(userId, teamId, propertyKey, propertyValue),
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: [teamKeys.getTeamProperty] });
			await queryClient.invalidateQueries({
				queryKey: [teamKeys.getTeamUsers],
			});
			return queryClient.invalidateQueries({
				queryKey: [paymentKeys.getTeamApprovers],
			});
		},
	});
};

export const useActivateTeamQuery = () => {
	return useMutation({
		mutationFn: TeamRepository.activateTeam,
		onSuccess: () => {
			return queryClient.invalidateQueries({ queryKey: teamKeys.getTeams({}) });
		},
	});
};

export const useDeActivateTeamQuery = () => {
	return useMutation({
		mutationFn: TeamRepository.deactivateTeam,
		onSuccess: () => {
			return queryClient.invalidateQueries({ queryKey: teamKeys.getTeams({}) });
		},
	});
};

export const useRemoveUserFromTeamQuery = () => {
	return useMutation<AxiosResponse, AxiosError, { userId: string; teamId: string }>({
		mutationFn: async ({ userId, teamId }) => {
			return TeamRepository.removeUserFromTeam(teamId, userId);
		},
		onSuccess: async (_data, variables) => {
			await queryClient.invalidateQueries({ queryKey: [teamKeys.getTeamUsers] });
			await queryClient.invalidateQueries({
				queryKey: [teamKeys.getDecoratedUser, { teamId: variables.teamId, userId: variables.userId }],
			});
		},
	});
};

async function getActiveTeamIdOrFallback(
	activeTeams: GetTeamsResponse[] | undefined,
	fallbackData: UserSettingResponse,
): Promise<UserSettingResponse> {
	if (!activeTeams?.length) {
		return fallbackData;
	}
	const activeTeam = activeTeams.find(team => team.state === 'ACTIVE');
	if (activeTeam) {
		await UserSettingRepository.saveUserSetting({ selectedTeamID: activeTeam.id });
		return [activeTeam.id];
	}
	return fallbackData;
}

async function geCurrentSelectedTeamIdOrTheFirstIdFormAnActiveTeam(settingName: UserSettingRequest) {
	let selectedTeamIdData: UserSettingResponse;
	try {
		const data = await UserSettingRepository.getUserSettings(settingName);
		if (settingName !== 'selectedTeamID') {
			return data;
		}
		selectedTeamIdData = data;
	} catch (error) {
		logger.error(error);
		selectedTeamIdData = [];
	}

	const user = await queryClient.ensureQueryData(userQuery);
	const isUserTsSupport = isSupportAdmin(user?.roles ?? []);

	if (isUserTsSupport) {
		// first try to get the current active selected team id
		try {
			const teamId = selectedTeamIdData?.[0] ?? '';
			const team = await queryClient.ensureQueryData(teamQuery({ teamId }));
			if (team.state === 'ACTIVE') {
				return [team?.id];
			}
			throw new Error(`No active team with id ${team?.id} found`);
		} catch {
			const activeTeams = await queryClient.ensureQueryData(teamsQuery({ state: 'active' }));
			return getActiveTeamIdOrFallback(activeTeams, selectedTeamIdData);
		}
	}

	const teamId = selectedTeamIdData?.[0] ?? '';
	const team = await queryClient.ensureQueryData(teamQuery({ teamId }));
	if (team.state === 'ACTIVE') {
		return selectedTeamIdData;
	}

	const teamData = await queryClient.ensureQueryData(teamsQuery({ state: 'active' }));
	return getActiveTeamIdOrFallback(teamData, selectedTeamIdData);
}

export const selectedTeamIdQuery = (settingName: UserSettingRequest) => {
	return queryOptions({
		queryKey: [userKeys.getUserSettings, settingName],
		queryFn: () => geCurrentSelectedTeamIdOrTheFirstIdFormAnActiveTeam(settingName),
		staleTime: setTime(10, 'seconds'),
	});
};

export type GetTeamsResponse = {
	createdBy: {
		companyAccountId: string;
		companyName: string;
		email: string;
		firstName: string;
		fullName: string;
		initials: string;
		language: string;
		lastName: string;
		roleId: string;
		roles: {
			roleId: string;
			securityGroupId: string;
			securityGroupTypeId: string;
		}[];
		state: string;
		userId: string;
	};
	createdDate: string;
	description: string;
	id: string;
	name: string;
	numberOfCards: number;
	isCardLimitExceeded: boolean;
	numberOfMembers: number;
	properties: OptionalTeamProperties;
	sourceCards: {
		billingAddress: {
			addressLine1: string;
			city: string;
			countryCode: string;
			region: string;
			zipCode: string;
		};
		currency: string;
		id: string;
		lastDigits: string;
		name: string;
		provider: string;
		status: string;
		label: string;
		providerLabel: string;
	}[];
	state: string;
	users: User[];
	teamsCount: number;
	metrics?: [
		{
			approvedAmount: number;
			spentAmount: number;
			availableBalance: number;
			currency: string;
			activeCardCount: number;
		},
	];
	teamMembersCount: number;
};

export type GetTeamsArgs = {
	state?: 'active' | 'inactive' | undefined;
	limit?: number | null | undefined; // if I send limit > 10 it throws error in back-end
	offset?: number | null | undefined; // currentPage is offset * limit
};

export const allTeamsQuery = queryOptions({
	queryKey: teamKeys.getAllTeams(),
	queryFn: TeamRepository.getAllTeams,
});

export const useGetAllTeamsQuery = () => {
	return useQuery(allTeamsQuery);
};

export const teamsQuery = (args?: GetTeamsArgs) => {
	const { state = 'active', limit = null, offset = null } = args ?? {};
	return queryOptions({
		queryKey: teamKeys.getTeams({ state, limit, offset }),
		queryFn: () => TeamRepository.getTeamsPage({ state, limit, offset }),
		enabled: Boolean(state),
	});
};

type TeamId = { teamId: string };

export const teamQuery = ({ teamId }: TeamId) =>
	queryOptions({
		queryKey: [teamKeys.getTeam, { teamId }],
		queryFn: () => TeamRepository.getTeam({ teamId }),
		enabled: !!teamId,
	});

export const useGetTeamQuery = ({ teamId }) => useQuery(teamQuery({ teamId }));

export const getDecoratedUserQuery = ({ teamId, userId, isEnabled = true }) =>
	queryOptions({
		queryKey: [teamKeys.getDecoratedUser, { teamId, userId }],
		queryFn: () => TeamRepository.getDecoratedUser({ teamId, userId }),
		enabled: isEnabled && Boolean(teamId && userId),
	});

/* deprecated use currentTeamQuery instead */
export const useGetCurrentTeamQuery = (existingTeams: GetTeamsResponse[], teamId: string[] | undefined) => {
	const [teamQuery, teamsQuery] = useQueries({
		queries: [
			{
				queryKey: [teamKeys.getTeam, { teamId }],
				queryFn: () => TeamRepository.getTeam({ teamId: teamId || '' }),
				enabled: Array.isArray(teamId) && Boolean(teamId?.[0]),
			},
			{
				queryKey: teamKeys.getTeams({ state: 'active' }),
				queryFn: async () => (await TeamRepository.getTeams({ state: 'active', limit: 10, page: 0 })) ?? [],
				enabled: Array.isArray(existingTeams) && existingTeams.length === 0 && !teamId,
			},
		],
	});

	if (teamQuery.isLoading || teamsQuery.isLoading) {
		return { data: null, isLoading: true };
	}

	if (teamQuery.error || teamsQuery.error) {
		return { data: null, error: teamQuery.error || teamsQuery.error, isLoading: false };
	}

	if (!_.isEmpty(teamQuery.data)) {
		return { data: teamQuery.data, isLoading: false };
	} else if (Array.isArray(existingTeams) && existingTeams.length > 0) {
		return { data: existingTeams[0], isLoading: false };
	} else if (!_.isEmpty(teamsQuery.data)) {
		return {
			data: teamsQuery.data?.[0] || {},
			isLoading: false,
		};
	}

	return {
		data: teamsQuery.data?.[0] || {},
		isLoading: false,
	};
};

export type TeamUsers = {
	numPages: number;
	users: User[];
	itemCount: number;
	pageId: number;
};

export const getTeamUsersQuery = ({ teamId, query = '' }) => {
	return queryOptions({
		queryKey: [teamKeys.getTeamUsers, teamId, query],
		queryFn: () => TeamRepository.getTeamUsers(teamId, query),
		enabled: !!teamId,
	});
};

export const useGetTeamUsersQuery = ({ limit = 10, page = 1, teamId, searchParam = '' }) => {
	let query = '?';
	query = query + `limit=${limit}`;
	query = query + `&page=${page}`;
	if (searchParam) {
		query = query + `&searchParam=${searchParam}`;
	}

	const queryInfo = getTeamUsersQuery({ teamId, query });

	return useQuery({
		...queryInfo,
		enabled: Boolean(teamId),
	});
};

export const useGetTeamRequestLimitQuery = (teamId: string) => {
	return useQuery({
		queryKey: [
			teamKeys.getTeamProperty,
			{
				teamId,
				propertyKey: teamFeaturesConstants.requestLimit,
			},
		],
		queryFn: () =>
			TeamRepository.getTeamProperty({
				teamId,
				propertyKey: teamFeaturesConstants.requestLimit,
			}),
		enabled: !!teamId,
	});
};

export const useGetTeamPropertyQuery = teamId => {
	return useQuery({
		queryKey: [teamKeys.getSelectedTeamProperty, teamId, teamFeaturesConstants.pauseVirtualCardCreation],
		queryFn: async () => {
			const result = await TeamRepository.getTeamProperty({
				teamId,
				propertyKey: teamFeaturesConstants.pauseVirtualCardCreation,
			});
			const value = result ? 'true' : 'false';
			useZustand.getState().setSelectedTeamProperty(value);
			return value;
		},
		enabled: Boolean(teamId),
	});
};

export const getOooSettingsQuery = ({ teamId, userId }: { userId: string; teamId: string }) => ({
	queryKey: [teamKeys.getOooSettings(teamId, userId)],
	queryFn: () => TeamRepository.getOooSettings({ teamId, userId }),
	enabled: Boolean(teamId),
});

export const useSetUserTeamProperties = () => {
	return useMutation({
		mutationFn: ({
			settings,
			teamId,
			userId,
		}: {
			settings: Partial<TeamProperties>[];
			teamId: string;
			userId?: string;
		}) =>
			TeamRepository.setUserTeamProperties({
				properties: settings,
				teamId,
				userId,
			}),
	});
};
